import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, Eye, ChevronRight } from 'lucide-react';
import PageTemplate from '../components/Common/PageTemplate';
import haberlerData from '../data/Haberler.json';

const NewsPage = () => {
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      
      // JSON dosyasından verileri yükle ve boş kayıtları filtrele
      const filteredData = haberlerData.filter(haber => 
        haber && 
        haber.Baslik && 
        haber.Baslik.trim() !== '' && 
        haber.Ozet && 
        haber.Ozet.trim() !== ''
      );

      // JSON field'larını beklenen field'lara map et
      const mappedData = filteredData.map((haber, index) => ({
        id: index + 1, // ID yoksa index kullan
        title: haber.Baslik,
        summary: haber.Ozet,
        content: haber.Ozet, // summary ile content aynı
        created_at: haber.Tarih,
        image: haber.ResimURL,
        external_url: haber.Link,
        author: null, // JSON'da yok
        category: null // JSON'da yok
      }));

      setNews(mappedData);
      setError(null);
    } catch (error) {
      console.error('Haberler yüklenirken hata:', error);
      setError(error.message);
      setNews([]); // Hata durumunda boş array
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    if (dateString.includes('-')) {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return dateString; // Eğer zaten Türkçe formatsa olduğu gibi döndür
  };

  if (loading) {
    return (
      <PageTemplate title={t('pages.news.title')} breadcrumb={[{ title: 'Anasayfa', href: '/' }, { title: t('pages.news.title') }]}>
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hacettepe-blue"></div>
        </div>
      </PageTemplate>
    );
  }

  if (error) {
    return (
      <PageTemplate title={t('pages.news.title')} breadcrumb={[{ title: 'Anasayfa', href: '/' }, { title: t('pages.news.title') }]}>
        <div className="text-center py-12">
          <div className="text-red-500 text-lg mb-4">{t('pages.news.loadError')} {error}</div>
          <button
            onClick={fetchNews}
            className="bg-hacettepe-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('pages.news.retry')}
          </button>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title={t('pages.news.title')}
      breadcrumb={[{ title: 'Anasayfa', href: '/' }, { title: t('pages.news.title') }]}
    >
      <div className="space-y-8">
        {news.length === 0 ? (
          <div className="text-center py-12">
            <Eye size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('pages.news.noNewsYet')}</h3>
            <p className="text-gray-500">{t('pages.news.noNewsDesc')}</p>
          </div>
        ) : (
          <div className="grid gap-8">
            {news.map((newsItem) => (
              <article key={newsItem.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                <div className="md:flex">
                  {newsItem.image && (
                    <div className="md:w-1/3">
                      <img 
                        src={newsItem.image} 
                        alt={newsItem.title}
                        className="w-full h-48 md:h-full object-cover"
                      />
                    </div>
                  )}
                  <div className={`p-6 ${newsItem.image ? 'md:w-2/3' : 'w-full'}`}>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        {formatDate(newsItem.created_at)}
                      </span>
                      {newsItem.author && (
                        <span className="flex items-center">
                          <User size={16} className="mr-1" />
                          {newsItem.author}
                        </span>
                      )}
                      {newsItem.category && (
                        <span className="bg-hacettepe-blue text-white px-2 py-1 rounded text-xs">
                          {newsItem.category.name || newsItem.category}
                        </span>
                      )}
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-800 mb-3 leading-tight">
                      {newsItem.title}
                    </h2>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {newsItem.summary || (newsItem.content ?
                        (newsItem.content.length > 200
                          ? `${newsItem.content.substring(0, 200)}...`
                          : newsItem.content
                        ) : t('pages.news.noSummary')
                      )}
                    </p>

                    {newsItem.external_url ? (
                      <a
                        href={newsItem.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-hacettepe-blue hover:text-blue-700 font-medium"
                      >
                        {t('pages.news.viewNews')}
                        <ChevronRight size={16} className="ml-1" />
                      </a>
                    ) : (
                      <Link
                        to={`/haberler/${newsItem.id}`}
                        className="inline-flex items-center text-hacettepe-blue hover:text-blue-700 font-medium"
                      >
                        {t('pages.news.readMore')}
                        <ChevronRight size={16} className="ml-1" />
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </PageTemplate>
  );
};

export default NewsPage; 